import { allProjects, projectEmails, listMailAssignments, getByPath } from '../db.js';
import { escapeHtml, safeUrl } from '../ui.js';
import { renderCompose } from './mail-compose.js';

// CORRESPONDENCE — Files/Mail Module, artboard 3a (main panel only).
// A job's written history in one scroll: sent and received in the SAME column,
// distinguished by a rule on the left (navy = received, grey = sent) rather than
// a colour fill, a chip, or an alignment flip. Calmer than the inbox — wider
// measure, full paragraphs, no keyboard furniture.
//
// files.js owns the shared project HEADER + the six-tab bar; this module renders
// ONLY the correspondence body (the flex row: scroll + right column) into the
// swappable panel, so there is never a duplicate header. See files.js part B.
//
// House rendering-safety rule: every data value is escaped through escapeHtml();
// any data-built href would go through safeUrl(). We deliberately DO NOT link out
// to Gmail (no "open in Gmail" escape per the binding rules) — threads open only
// via onOpenThread(threadId).

// ---- tokens / small style consts ------------------------------------------
const MONO = 'font-family:var(--font-technical);';
const EYEBROW = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;';

// ---- pure helpers ---------------------------------------------------------
// Several are EXPORTED: mail-reader.js (the shared reader used by enquiries,
// project detail and the Gmail panels) reuses this file's parsing + entry
// pattern so there is exactly one email grammar in Track.
const pad2 = (n) => String(n).padStart(2, '0');
const isAsp = (from) => /@aspconsulting\.co\.uk/i.test(String(from || ''));

// A Date or null (never NaN) from an ISO/date-ish value.
export function toDate(v) {
  if (!v) return null;
  const d = (v && typeof v.toDate === 'function') ? v.toDate() : new Date(v);
  return isNaN(d) ? null : d;
}
const timeOf = (d) => (d ? d.getTime() : 0);

// Parse a human NAME out of an rfc822 `from` string: "Name <a@b>" → Name;
// a bare address → prettified local part; otherwise the string as-is.
export function parseName(from) {
  const s = String(from || '').trim();
  if (!s) return '—';
  const m = s.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/);
  if (m && m[1].trim()) return m[1].trim();
  if (/^[^@\s]+@[^@\s]+$/.test(s)) {
    return s.split('@')[0].replace(/[._]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
}
// The bare address out of a `from` string (for the Received "detail" line).
export function extractEmail(from) {
  const s = String(from || '');
  const m = s.match(/<([^>]+)>/) || s.match(/([^\s<>]+@[^\s<>]+)/);
  return m ? m[1] : '';
}

// Direction → 'Sent' | 'Received'. Received → navy/Received; Issued OR an ASP
// sender → grey/Sent; unknown (manual assignments) is inferred from the domain.
export function dirOf(from, direction) {
  const d = String(direction || '').toLowerCase();
  if (d === 'issued' || d === 'sent') return 'Sent';
  if (d === 'received') return 'Received';
  return isAsp(from) ? 'Sent' : 'Received';
}

// Best-effort dedupe key when there is no threadId.
const subjFromKey = (subject, from) =>
  `${String(subject || '').trim().toLowerCase()}|${String(from || '').trim().toLowerCase()}`;

// Monday 00:00 of the week containing d (UK week starts Monday).
export function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // Mon=0 … Sun=6
  x.setDate(x.getDate() - day);
  return x;
}
// "DD/MM HH:MM" mono stamp (3a line 841).
export function stamp(d) {
  return d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
}
// "4 March" style for the summary line.
const longDate = (d) => (d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : '—');

// The reading paragraph: snippet preferred, else a trimmed body. NOT truncated
// to one line (this is the calm, wide-measure read); very long bodies capped.
// The message itself, not Gmail's snippet: the snippet runs lines together
// ("TEST V1KARAN SINGH, BSc., MIEDMANAGING DIRECTOR+44…", 08/10/2026). The
// stored plain body keeps its line breaks; it is cut at the quoted history and
// at the signature or the confidentiality footer, so the entry shows what was
// actually written. Falls back to the snippet when there is no body.
export function bodyPara(e) {
  let s = String(e.body || '').replace(/\r\n/g, '\n');
  if (s.trim()) {
    const lines = [];
    for (const l of s.split('\n')) {
      if (/^\s*On .{3,200} wrote:\s*$/i.test(l) || /^\s*From:\s/i.test(l) || /^\s*>/.test(l)
        || /^\s*--\s*$/.test(l) || /^\s*Confidential and intended/i.test(l)
        || /^\s*[A-Z][A-Z .,'’-]{5,}(,?\s*(BSc|MSc|MEng|CEng|MIStructE|MICE|MIED)\.?)*\s*$/.test(l)
        || /^\s*\[image:/i.test(l)) break;
      lines.push(l);
    }
    s = lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }
  if (!s) s = String(e.snippet || '').trim();
  if (!s) return '';
  return s.length > 600 ? s.slice(0, 600).replace(/\s+\S*$/, '') + '…' : s;
}

// Normalise an archiver projectEmails row → the shared entry shape.
export function normArchiver(e) {
  const from = String(e.from || '');
  return {
    key: e.threadId || e.id || subjFromKey(e.subject, from),
    threadId: e.threadId || '',
    subject: e.subject || '(no subject)',
    from, senderName: parseName(from), email: extractEmail(from),
    to: e.to || '',
    dir: dirOf(from, e.direction),
    snippet: e.snippet || '', body: e.body || '',
    date: toDate(e.date),
    msgCount: Number(e.msgCount) || 1,
    isManual: false,
    // projectEmails does NOT carry attachment-filing data (see attachmentChips).
    attachments: Array.isArray(e.attachments) ? e.attachments : null,
  };
}
// Normalise a manual mailAssignment → the SAME entry shape (shown identically).
function normManual(a) {
  const from = String(a.from || '');
  return {
    key: a.threadId || a.id || subjFromKey(a.subject, from),
    threadId: a.threadId || '',
    subject: a.subject || '(no subject)',
    from, senderName: parseName(from), email: extractEmail(from),
    to: '',
    dir: isAsp(from) ? 'Sent' : 'Received', // infer from sender (no direction on assignments)
    snippet: '', body: '',
    date: toDate(a.assignedAt),
    msgCount: 1,
    isManual: true,
    attachments: null,
  };
}

// ---- attachment chips (3a lines 845–851, 864–869) -------------------------
// OPEN: filed/unfiled attachment chips need cross-referencing the item-6 Drive
// filings (appProperties.filedFromThread/docNumber) — not available from
// projectEmails; shown only where derivable. We render the STRUCTURE faithfully
// but never fabricate a document number: with no attachment data on an entry
// this returns '' (nothing for that entry), and only populates when a future
// entry.attachments array arrives carrying real { docNumber } / unfiled counts.
function attachmentChips(e) {
  const atts = e.attachments;
  if (!Array.isArray(atts) || !atts.length) return ''; // unknown → render nothing
  const markCalc = `<svg width="14" height="14" viewBox="0 0 26 26" fill="none" stroke="var(--ink)" stroke-width="2.4"><rect x="5" y="3" width="16" height="20" rx="1"></rect><path d="M9 10h8M9 14h8M9 18h4"></path></svg>`;
  const filed = atts.filter((a) => a && a.docNumber);
  const unfiled = atts.filter((a) => a && !a.docNumber).length;
  const chips = filed.map((a) => `
        <span style="border: 1px solid var(--hair); border-radius: 8px; padding: 7px 11px; display: flex; align-items: center; gap: 9px; font-size: 12px;">
          ${markCalc}
          <span style="${MONO}">${escapeHtml(a.docNumber)}</span><span style="color: var(--ink-3);">filed</span>
        </span>`);
  if (unfiled > 0) {
    chips.push(`<span style="border: 1px dashed var(--brand-orange); color: var(--brand-orange); border-radius: 8px; padding: 7px 11px; font-size: 12px;">${unfiled} attachment${unfiled === 1 ? '' : 's'} not filed</span>`);
  }
  if (!chips.length) return '';
  return `<div style="display: flex; gap: 9px; padding-top: 4px;">${chips.join('')}</div>`;
}

// ---- one entry (3a lines 835–870) -----------------------------------------
export function entryHtml(e) {
  const received = e.dir === 'Received';
  const border = received ? 'var(--ink)' : 'var(--hair-strong)';
  const label = received ? 'Received' : 'Sent';
  // detail: Sent → "to {to}"; Received → the person/company (the address).
  let detail = '';
  if (!received && e.to) detail = 'to ' + e.to;
  else if (received && e.email && e.email.toLowerCase() !== e.senderName.toLowerCase()) detail = e.email;
  const para = bodyPara(e);
  const clickable = !!e.threadId;
  return `
            <div class="mc-entry" data-dir="${escapeHtml(e.dir)}"${clickable ? ` data-thread="${escapeHtml(e.threadId)}"` : ''} style="border-left: 2px solid ${border}; padding: 2px 0 2px 22px; display: flex; flex-direction: column; gap: 9px;${clickable ? ' cursor: pointer;' : ''}">
              <div style="display: flex; align-items: baseline; gap: 12px;">
                <span style="${EYEBROW}color: var(--ink-3);">${label}</span>
                <span style="font-size: 15px; font-weight: 500;">${escapeHtml(e.senderName)}</span>
                ${detail ? `<span style="font-size: 13px; color: var(--ink-3);">${escapeHtml(detail)}</span>` : ''}
                <div style="flex: 1;"></div>
                <span style="${MONO}font-size: 12px; color: var(--ink-3);">${escapeHtml(stamp(e.date))}</span>
              </div>
              <span style="font-size: 17px; font-weight: 500; letter-spacing: -0.015em;">${escapeHtml(e.subject)}</span>
              ${para ? `<span style="font-size: 15px; line-height: 1.6; color: var(--ink-2); max-width: 84ch; text-wrap: pretty; white-space: pre-line;">${escapeHtml(para)}</span>` : ''}
              ${attachmentChips(e)}
            </div>`;
}

// A bucket = hairline label (blue 10px caps, 3a lines 830–833) + its entries.
// Wrapped so an empty bucket (after live filtering) can be hidden whole.
export function bucketHtml(name, entries) {
  return `
            <div class="mc-bucket" style="display: flex; flex-direction: column; gap: 26px;">
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="${EYEBROW}color: var(--link);">${escapeHtml(name)}</span>
                <span style="flex: 1; height: 1px; background: var(--hair);"></span>
              </div>
              ${entries.map(entryHtml).join('')}
            </div>`;
}

// A filter row in the right column (3a lines 907–910).
function filterRow(label, dataDir, checked, disabled) {
  const box = checked
    ? `<span class="mc-box" style="width: 15px; height: 15px; border-radius: 4px; background: var(--ink-fill); color:var(--ink-fill-ink); display: flex; align-items: center; justify-content: center; font-size: 10px;">&#10003;</span>`
    : `<span class="mc-box" style="width: 15px; height: 15px; border-radius: 4px; border: 1.5px solid var(--hair-strong);"></span>`;
  const attrs = disabled ? '' : ` class="mc-filter" data-dir="${dataDir}" style="cursor: pointer;"`;
  const style = disabled ? ' style="opacity: 0.5;"' : '';
  return `<span${disabled ? style : attrs} data-checked="${checked ? '1' : '0'}"><span style="font-size: 13.5px; display: flex; align-items: center; gap: 9px;">${box}${escapeHtml(label)}</span></span>`;
}

const LOADING = '<div style="padding:48px 40px;color:var(--ink-3);font-size:13px;">Loading…</div>';

export async function renderCorrespondence(mount, session, projectNumber, { project, onOpenThread, onWrite, onOpenProject } = {}) {
  // Immediate loading state before any await (house rule).
  mount.innerHTML = LOADING;

  // Resolve the project if the caller didn't pass one.
  let proj = project;
  if (!proj) {
    const projects = await allProjects().catch(() => []);
    proj = projects.find((p) => String(p.number) === String(projectNumber)) || null;
  }
  const number = String((proj && proj.number) || projectNumber || '');

  // The A-number this job was enquired under. A thread keeps the tag it was
  // given for the life of the job, so correspondence about the project can be
  // indexed under the enquiry number — read both or it disappears the moment
  // the enquiry becomes a project.
  let enqNo = null;
  if (proj && proj.enquiryRef) {
    const rel = String(proj.enquiryRef).replace(/^organisations\/[^/]+\//, '');
    const enq = await getByPath(rel).catch(() => null);
    enqNo = enq && enq.number ? String(enq.number) : null;
  }

  // ---- data ---------------------------------------------------------------
  // projectEmails is the archiver's shared TRACK// index — the substantive source.
  const [emails, assignments] = await Promise.all([
    projectEmails(number, 200, enqNo).catch(() => []),
    listMailAssignments().catch(() => ({})),
  ]);

  const entries = emails.map(normArchiver);

  // UNION in manual inbox→job assignments that the archiver hasn't already
  // indexed. Dedupe heuristic: skip any assignment whose threadId is already
  // represented; else skip if its subject+from key matches an existing entry.
  // Anything left is a manual-only item, normalised to the same shape and shown
  // identically (direction inferred from the ASP domain, assignedAt as its date).
  const seenThreads = new Set(entries.map((e) => e.threadId).filter(Boolean));
  const seenSubjFrom = new Set(entries.map((e) => subjFromKey(e.subject, e.from)));
  Object.values(assignments).forEach((a) => {
    if (!a || String(a.job) !== number) return;
    if (a.threadId && seenThreads.has(a.threadId)) return;
    const key = subjFromKey(a.subject, a.from);
    if (seenSubjFrom.has(key)) return;
    if (a.threadId) seenThreads.add(a.threadId);
    seenSubjFrom.add(key);
    entries.push(normManual(a));
  });

  // Newest first overall.
  entries.sort((a, b) => timeOf(b.date) - timeOf(a.date));

  // ---- empty state --------------------------------------------------------
  if (!entries.length) {
    mount.innerHTML = `
      <div style="flex: 1; display: flex; min-height: 0;">
        <div style="flex: 1; min-width: 0; padding: 30px 40px 40px; display: flex; flex-direction: column; gap: 14px;">
          <span style="font-size: 17px; font-weight: 500; letter-spacing: -0.015em;">No correspondence filed against ${escapeHtml(number)} yet</span>
          <span style="font-size: 15px; line-height: 1.6; color: var(--ink-2); max-width: 84ch; text-wrap: pretty;">Email tagged <span style="${MONO}">TRACK// ${escapeHtml(number)}</span>, or assigned to this project from the inbox, will collect here as the project's written history.</span>
        </div>
      </div>`;
    return;
  }

  // ---- stats (top row) ----------------------------------------------------
  const msgTotal = entries.reduce((s, e) => s + e.msgCount, 0);
  const threadTotal = new Set(entries.map((e) => e.threadId || e.key)).size;
  const dated = entries.map((e) => e.date).filter(Boolean);
  const earliest = dated.length ? new Date(Math.min(...dated.map((d) => d.getTime()))) : null;

  // ---- buckets: This week / Last week / Earlier (relative to now) ----------
  const now = new Date();
  const thisWeek = startOfWeek(now);
  const lastWeek = new Date(thisWeek); lastWeek.setDate(lastWeek.getDate() - 7);
  const buckets = { 'This week': [], 'Last week': [], 'Earlier': [] };
  entries.forEach((e) => {
    const t = e.date ? e.date.getTime() : 0;
    if (t >= thisWeek.getTime()) buckets['This week'].push(e);
    else if (t >= lastWeek.getTime()) buckets['Last week'].push(e);
    else buckets['Earlier'].push(e);
  });
  // (entries are already newest-first, so each bucket keeps that order)
  const bucketsHtml = ['This week', 'Last week', 'Earlier']
    .filter((k) => buckets[k].length)
    .map((k) => bucketHtml(k, buckets[k])).join('');

  // ---- who is on this job (senders by volume, top 6) ----------------------
  const byPerson = new Map();
  entries.forEach((e) => byPerson.set(e.senderName, (byPerson.get(e.senderName) || 0) + e.msgCount));
  const people = Array.from(byPerson.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);

  // ---- render (main panel only — files.js owns header + tabs) -------------
  mount.innerHTML = `
      <div style="flex: 1; display: flex; min-height: 0;">

        <div style="flex: 1; min-width: 0; padding: 30px 40px 40px; display: flex; flex-direction: column; gap: 26px;">

          <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 20px;">
            <span style="font-size: 15px; color: var(--ink-2);">${msgTotal} message${msgTotal === 1 ? '' : 's'} since ${escapeHtml(longDate(earliest))}, across ${threadTotal} thread${threadTotal === 1 ? '' : 's'}.</span>
            <span id="mc-write" style="background: var(--ink-fill); color:var(--ink-fill-ink); border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 500; cursor: pointer; flex: none;">Write to this project</span>
          </div>

          <div id="mc-scroll" style="display: flex; flex-direction: column; gap: 26px;">
            ${bucketsHtml}
            <div id="mc-empty" style="display: none; padding: 18px 0; color: var(--ink-3); font-size: 13px;">Nothing matches those filters.</div>
          </div>

        </div>

        <div style="width: 300px; flex: none; border-left: 1px solid var(--hair); background: var(--brief-bg); padding: 30px 26px; display: flex; flex-direction: column; gap: 24px;">
          <div style="display: flex; flex-direction: column; gap: 11px;">
            <span style="${EYEBROW}color: var(--ink-3);">Who is on this project</span>
            <div style="display: flex; flex-direction: column; gap: 8px;">
              ${people.map(([name, n]) => `<span style="font-size: 13.5px; display: flex; justify-content: space-between; gap: 12px;">${escapeHtml(name)}<span style="${MONO}font-size: 12px; color: var(--ink-3);">${n}</span></span>`).join('') || '<span style="font-size: 13.5px; color: var(--ink-3);">&#8212;</span>'}
            </div>
          </div>
          <div style="height: 1px; background: var(--hair);"></div>
          <div style="display: flex; flex-direction: column; gap: 11px;">
            <span style="${EYEBROW}color: var(--ink-3);">Show</span>
            <div style="display: flex; flex-direction: column; gap: 8px;">
              ${filterRow('Received', 'Received', true, false)}
              ${filterRow('Sent', 'Sent', true, false)}
              ${/* attachment-filing data is not in projectEmails, so these two stay disabled — see attachmentChips OPEN note */ ''}
              ${filterRow('Only with attachments', '', false, true)}
              ${filterRow('Only unfiled attachments', '', false, true)}
            </div>
          </div>
          <div style="height: 1px; background: var(--hair);"></div>
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <span style="${EYEBROW}color: var(--brand-orange);">Needs filing</span>
            ${/* Honest placeholder: filed/unfiled counts require the item-6 Drive
                 filings cross-reference, which isn't available here — so we do NOT
                 invent a number (the artboard's "Four attachments" is mock copy). */ ''}
            <span style="font-size: 13.5px; line-height: 1.5; color: var(--ink-2);">Filing status isn&#39;t tracked in correspondence yet &#8212; a document&#39;s number comes from the register.</span>
          </div>
        </div>

      </div>`;

  // ---- interactivity ------------------------------------------------------
  // Write to this project = compose (item 8, artboard 5d), opened cold against
  // this job (which pre-fills and locks the job chip; the picker and filing all
  // key off it). A router may still pass onWrite to intercept.
  const writeBtn = mount.querySelector('#mc-write');
  if (writeBtn) writeBtn.addEventListener('click', () => {
    if (typeof onWrite === 'function') { onWrite(); return; }
    renderCompose(session, { mode: 'new', job: number, project: proj });
  });

  // Open a thread (item-6 thread view) when an entry carries a threadId.
  mount.querySelectorAll('.mc-entry[data-thread]').forEach((el) => {
    el.addEventListener('click', () => {
      const tid = el.getAttribute('data-thread');
      if (tid && typeof onOpenThread === 'function') onOpenThread(tid);
    });
    el.addEventListener('mouseenter', () => { el.style.background = 'var(--row-hover)'; });   // was a fixed white: blank text in dark mode
    el.addEventListener('mouseleave', () => { el.style.background = ''; });
  });

  // Live Received/Sent filter — hide entries by direction, then hide any bucket
  // that has gone empty; show the empty message if the whole scroll is empty.
  const shownDir = { Received: true, Sent: true };
  const emptyMsg = mount.querySelector('#mc-empty');
  function applyFilter() {
    let anyShown = 0;
    mount.querySelectorAll('.mc-bucket').forEach((bucket) => {
      let vis = 0;
      bucket.querySelectorAll('.mc-entry').forEach((el) => {
        const hit = shownDir[el.getAttribute('data-dir')] !== false;
        el.style.display = hit ? '' : 'none';
        if (hit) vis++;
      });
      bucket.style.display = vis ? '' : 'none';
      anyShown += vis;
    });
    if (emptyMsg) emptyMsg.style.display = anyShown ? 'none' : 'block';
  }
  mount.querySelectorAll('.mc-filter').forEach((el) => {
    el.addEventListener('click', () => {
      const dir = el.getAttribute('data-dir');
      const on = el.getAttribute('data-checked') === '1';
      const next = !on;
      shownDir[dir] = next;
      el.setAttribute('data-checked', next ? '1' : '0');
      // repaint the checkbox visual
      const box = el.querySelector('.mc-box');
      if (box) box.setAttribute('style', next
        ? 'width: 15px; height: 15px; border-radius: 4px; background: var(--ink-fill); color:var(--ink-fill-ink); display: flex; align-items: center; justify-content: center; font-size: 10px;'
        : 'width: 15px; height: 15px; border-radius: 4px; border: 1.5px solid var(--hair-strong);');
      if (box) box.innerHTML = next ? '&#10003;' : '';
      applyFilter();
    });
  });

  // Mobile 3b is deferred to the item-10 mobile pass.
}
